import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image as ImageIcon, XCircle } from 'lucide-react';
import { Button } from '../shared';

const EventStartModal = ({ isOpen, onClose, onStart, isLoading }) => {
  const [notes, setNotes] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [previews, setPreviews] = useState([]);
  const wasLoadingRef = useRef(false);
  const onCloseRef = useRef(onClose);

  // Keep onClose ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleClose = () => {
    setNotes('');
    setScreenshots([]);
    setPreviews([]);
    onCloseRef.current();
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNotes('');
      setScreenshots([]);
      setPreviews([]);
      wasLoadingRef.current = false;
    }
  }, [isOpen]);

  // Close modal when loading completes (save finished)
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading && isOpen) {
      // Loading just finished, close the modal
      handleClose();
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, isOpen]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValidType = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      alert('Some files were rejected. Only JPEG, PNG, JPG, GIF images up to 5MB are allowed.');
    }

    // Limit to 5 total screenshots
    const totalScreenshots = screenshots.length + validFiles.length;
    if (totalScreenshots > 5) {
      alert('Maximum 5 screenshots allowed. Only the first 5 will be uploaded.');
      const remainingSlots = 5 - screenshots.length;
      validFiles.splice(remainingSlots);
    }

    setScreenshots([...screenshots, ...validFiles]);

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => [...prev, { file, url: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeScreenshot = (index) => {
    setScreenshots(screenshots.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least 1 screenshot is required
    if (!screenshots || screenshots.length === 0) {
      alert('Please upload at least 1 screenshot');
      return;
    }
    
    try {
      // Call onStart and wait for it to complete if it returns a promise
      const result = onStart({
        notes: notes.trim() || null,
        screenshots: screenshots,
      });
      
      // If onStart returns a promise, wait for it
      if (result && typeof result.then === 'function') {
        await result;
      }
      
      // Close modal after successful submission (if not already closed by parent)
      // The parent component will handle closing via useEffect
    } catch (error) {
      // Error is handled by the parent component
      console.error('Error starting event:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold text-gray-900">Start Event</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="Add any notes about starting the event..."
                maxLength={2000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {notes.length}/2000 characters
              </p>
            </div>

            {/* Screenshots */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Screenshots <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  id="screenshots"
                  multiple
                  accept="image/jpeg,image/png,image/jpg,image/gif"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="screenshots"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Upload className="w-10 h-10 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-1">
                    Click to upload screenshots
                  </p>
                  <p className="text-xs text-gray-500">
                    JPEG, PNG, JPG, GIF up to 5MB each (1-5 images required)
                  </p>
                </label>
              </div>

              {/* Preview Grid */}
              {previews.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {previews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview.url}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeScreenshot(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                disabled={isLoading}
              >
                Start Event
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventStartModal;

